import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { getSnapshotDetails } from "../../../app/api/trendSnapshot";
import { RootState } from "../../../app/store";
import { ReactComponent as LeftArrow } from "../../../assets/icons/leftArrow.svg";
import { ReactComponent as Download } from "../../../assets/icons/download.svg";
import { TrendCurveWindowType } from "../../../constants/constants";
import { handleTrendCurveDownload } from "../../../utils/trendSidebarGraphUtils/trendCurveDownloadUtils";
import { toast } from "react-toastify";
import {
  TrendCurveChartData,
  TrendItem,
} from "../../../interface/DesignerInterface";
import {
  filterActiveTrendCurvePeriods,
  filterTrendCurveByDateRange,
  getFactoredTrendCurveResponse,
  shouldPreserveAllCurvePeriods,
} from "../../../utils/trendSidebarGraphUtils/trendCurveDataUtils";
import {
  getSnapshotTrendCurveWindowLabel,
  TREND_CURVE_LIVE_FILTER_TYPE,
} from "../../../utils/trendSidebarGraphUtils/trendCurvesUtils";
import { setTrendCurveDatasetHighlight } from "../../../utils/trendSidebarGraphUtils/trendCurveChartRenderer";
import { filterArchivedGraphRows } from "../../../utils/archivedTrends";
import TrendTable from "../TrendsSidebar/TrendCurveTable";
import TrendCurvesGraph from "../TrendsSidebar/TrendCurvesGraph";
import { checkSnapshotStatus } from "../../../utils/snapshotHelpers";

interface ExpandViewTrendCurvesProps {
  isSidebarOpen?: boolean;
}

const ExpandViewSnapshotTrendCurveAnalysis: React.FC<
  ExpandViewTrendCurvesProps
> = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const [trendsCurveData, setTrendsCurveData] = useState<TrendCurveChartData>({
    labels: [],
    datasets: [],
  });
  const [trendsCurveRawData, setTrendsCurveRawData] = useState<TrendItem[]>([]);
  const [curveWindowType, setCurveWindowType] =
    useState<TrendCurveWindowType>("Month");
  const [selectedTNo, setSelectedTNo] = useState<number | null>(null);
  const [snapshotStatus, setSnapshotStatus] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const chartRef = useRef<SVGSVGElement | null>(null);
  const pageScrollRef = useRef<HTMLDivElement | null>(null);
  const graphContainerRef = useRef<HTMLDivElement | null>(null);

  const user = useSelector((state: RootState) => state.user.userInfo);
  const { id } = useParams();
  const numericSnapshotId = Number(id);

  const fetchDetails = async () => {
    setLoading(true);
    const detailsParams = {
      page: 1,
      numberOfTrends: 1,
      postsPerTrend: 1,
    };
    try {
      const snapDetails = await getSnapshotDetails(
        numericSnapshotId,
        user,
        detailsParams
      );
      const { status, isNotCompleted } = checkSnapshotStatus(snapDetails);
      setSnapshotStatus(status);
      if (isNotCompleted) {
        return;
      }
      const trendCurveResponse = snapDetails?.data.trendCurveData ?? [];
      const genericFilters = snapDetails?.data?.snapshot?.genericFilters;
      const graphsFilters = genericFilters?.graphsFilters;
      const snapshotCurveWindow =
        getSnapshotTrendCurveWindowLabel(graphsFilters);
      const preserveAllPeriods = shouldPreserveAllCurvePeriods(
        genericFilters?.preferenceFilters
      );

      setCurveWindowType(snapshotCurveWindow);
      setSelectedTNo(null);
      const archivedTrendNames = genericFilters?.archivedTrendNames || [];
      const filteredResponse = filterArchivedGraphRows<TrendItem>(
        trendCurveResponse,
        archivedTrendNames
      );
      const trimmedRawData = filterActiveTrendCurvePeriods(
        filteredResponse,
        undefined,
        preserveAllPeriods
      );
      // Keep only periods matching the dates selected in snapshot preferences.
      const dateFilteredRawData = filterTrendCurveByDateRange(
        trimmedRawData,
        genericFilters?.preferenceFilters
      );
      setTrendsCurveRawData(dateFilteredRawData);
      setTrendsCurveData(
        getFactoredTrendCurveResponse(
          dateFilteredRawData,
          snapshotCurveWindow,
          undefined,
          preserveAllPeriods
        )
      );
    } catch (error) {
      console.error("Error fetching snapshot details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchDetails();
    } else {
      setLoading(false);
    }
  }, [id, numericSnapshotId, user]);

  useEffect(() => {
    if (snapshotStatus === "pending" || snapshotStatus === "processing") {
      const interval = setInterval(() => {
        fetchDetails();
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [snapshotStatus]);

  const visibleChartData = useMemo((): TrendCurveChartData => {
    if (selectedTNo == null) return trendsCurveData;
    return {
      labels: trendsCurveData.labels,
      datasets: trendsCurveData.datasets.filter(
        dataset => dataset.tNo === selectedTNo
      ),
    };
  }, [trendsCurveData, selectedTNo]);

  const isSnapshotProcessing =
    snapshotStatus === "pending" || snapshotStatus === "processing";
  const isLoading = loading || isSnapshotProcessing;
  const hasChartData = trendsCurveData.datasets.length > 0;

  const handleRowClick = (tNo: number) => {
    setSelectedTNo(prev => (prev === tNo ? null : tNo));
    setTrendCurveDatasetHighlight(chartRef.current, null);
    pageScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      className={`bg-white fixed h-[calc(100vh-80px)] ml-2 ${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} transition-all duration-500 ease-in-out`}
    >
      <div className="flex flex-row justify-between mb-8">
        <div className="flex flex-row mt-6">
          <div>
            <LeftArrow
              className="text-lg ml-4 mt-4 cursor-pointer"
              onClick={() => navigate(`/trend-snapshot/details/${id}`)}
            />
          </div>
          <div className="transition-all  duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-4 mt-2.5 text-secondary ">
            Trend Curve
          </div>
        </div>
        <div className="mt-2 mr-4 flex items-end">
          <button
            onClick={() => {
              if (snapshotStatus !== "completed") {
                toast.error("Snapshot not ready for download", {
                  hideProgressBar: true,
                });
                return;
              }
              handleTrendCurveDownload(
                setDownloadLoading,
                visibleChartData,
                trendsCurveRawData,
                graphContainerRef.current ?? undefined,
                TREND_CURVE_LIVE_FILTER_TYPE,
                curveWindowType
              );
            }}
            disabled={
              downloadLoading ||
              isLoading ||
              !hasChartData ||
              snapshotStatus !== "completed"
            }
            title={
              snapshotStatus !== "completed"
                ? "Snapshot not ready"
                : "Download Trend Curve Report"
            }
            className="flex items-center gap-2 mr-4 border border-gray-200 rounded-md px-3 py-1.5 bg-white text-secondary hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {downloadLoading ? (
              <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
            ) : (
              <Download width={20} height={20} />
            )}
          </button>
          <div className="text-sm">
            <div>
              <span className="text-secondary">Filter:</span>{" "}
              {TREND_CURVE_LIVE_FILTER_TYPE}
            </div>
            <div>
              <span className="text-secondary">Curve Window:</span>{" "}
              {isLoading ? "—" : curveWindowType}
            </div>
          </div>
        </div>
      </div>

      <div
        ref={pageScrollRef}
        className="h-[calc(100vh-150px)] overflow-y-auto"
      >
        {isLoading ? (
          <div className="px-3 pb-8">
            <div className="my-3 flex items-center gap-2 text-sm text-gray-600">
              <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
              {isSnapshotProcessing
                ? "Snapshot is being generated…"
                : "Loading trend curve…"}
            </div>
            <Skeleton height={400} className="w-full" />
            <div className="mt-6 space-y-2">
              <Skeleton height={44} />
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} height={38} />
              ))}
            </div>
          </div>
        ) : hasChartData ? (
          <>
            <div
              ref={graphContainerRef}
              className="relative z-0 ml-3 mr-6 bg-white pb-2 pt-1"
            >
              {selectedTNo != null ? (
                <div className="mb-1 flex items-center justify-between px-1 text-xs text-gray-600">
                  <span>
                    Showing Trend No. <strong>{selectedTNo}</strong> only
                  </span>
                  <button
                    type="button"
                    className="font-medium text-secondary underline"
                    onClick={() => setSelectedTNo(null)}
                  >
                    Show all trends
                  </button>
                </div>
              ) : null}
              <TrendCurvesGraph
                ref={chartRef}
                trendCurveData={visibleChartData}
                trendsCurveExpandedView
                fillContainer
                showContributionLabels={selectedTNo != null}
              />
            </div>

            <div className="mt-6">
              <TrendTable
                trendsCurveRawData={trendsCurveRawData}
                curveWindowType={curveWindowType}
                datasets={trendsCurveData.datasets}
                selectedTNo={selectedTNo}
                onRowClick={handleRowClick}
                onRowHover={datasetIndex =>
                  setTrendCurveDatasetHighlight(chartRef.current, datasetIndex)
                }
              />
            </div>
          </>
        ) : (
          <div className="my-10 text-center text-gray-600">
            No Trend Curve Data
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpandViewSnapshotTrendCurveAnalysis;
